import React, { useState, useEffect } from 'react';
import { Search, Plus, Download, Filter, ChevronDown, Package, Calendar, Hash, Barcode, DollarSign } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from '../utils/axios';
import { API_URL } from '../config/api.config';

const ProductCatalogPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [activeFilters, setActiveFilters] = useState(new Set(['all']));
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Advanced filter states
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedStockStatus, setSelectedStockStatus] = useState(new Set());
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [selectedGstRates, setSelectedGstRates] = useState(new Set());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, searchTerm, sortBy, activeFilters, selectedCategories, selectedStockStatus, priceRange, selectedGstRates]);

  const fetchProducts = async () => {
    try {
      setLoading(true);

      // Fetch both products and categories
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/products`),
        axios.get(`${API_URL}/admin/categories`)
      ]);

      // Transform products to match our component structure
      const transformedProducts = productsRes.data.map(prod => {
        const quantity = prod.product?.quantity || 0;
        let stockStatus = 'out_of_stock';

        if (quantity > 10) {
          stockStatus = 'in_stock';
        } else if (quantity > 0) {
          stockStatus = 'low_stock';
        }

        return {
          id: prod.id,
          name: prod.product?.name || 'Unknown Product',
          category: prod.product?.categoryName || 'Uncategorized',
          image: '/a/60',
          sku: prod.product?.sku || `SKU-${prod.id?.slice(-6)}`,
          hsn: prod.product?.hsnCode || 'N/A',
          addedDate: new Date(prod.createdAt || Date.now()),
          stockStatus: stockStatus,
          stockCount: quantity,
          price: prod.product?.sellingPrice || prod.product?.purchasePricePerUnit || 0,
          sellingPrice: prod.product?.sellingPrice || 0,
          description: prod.product?.description || '',
          gstPercentage: (prod.product?.gstPercentage !== undefined && prod.product?.gstPercentage !== null) ? prod.product.gstPercentage : 0,
          supplier: prod.supplier?.shopName || 'Unknown Supplier'
        };
      });

      setProducts(transformedProducts);
      setCategories(categoriesRes.data);
      console.log('📦 Loaded products:', transformedProducts.length);
      console.log('📂 Loaded categories:', categoriesRes.data.length);

    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = () => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Quick filter status filters (only if advanced filters are not active)
    if (!activeFilters.has('all') && selectedStockStatus.size === 0) {
      filtered = filtered.filter(product => {
        if (activeFilters.has('in_stock') && product.stockStatus === 'in_stock') return true;
        if (activeFilters.has('low_stock') && product.stockStatus === 'low_stock') return true;
        if (activeFilters.has('out_of_stock') && product.stockStatus === 'out_of_stock') return true;
        return false;
      });
    }

    // Quick filter category filters (only if advanced filters are not active)
    const categoryFilters = Array.from(activeFilters).filter(filter =>
      !['all', 'in_stock', 'low_stock', 'out_of_stock'].includes(filter)
    );
    if (categoryFilters.length > 0 && selectedCategories.size === 0) {
      filtered = filtered.filter(product =>
        categoryFilters.some(category =>
          product.category.toLowerCase().includes(category.toLowerCase())
        )
      );
    }

    // Advanced Filters

    // Category filter (advanced)
    if (selectedCategories.size > 0) {
      filtered = filtered.filter(product =>
        selectedCategories.has(product.category)
      );
    }

    // Stock status filter (advanced)
    if (selectedStockStatus.size > 0) {
      filtered = filtered.filter(product =>
        selectedStockStatus.has(product.stockStatus)
      );
    }

    // Price range filter
    if (priceRange.min !== '' || priceRange.max !== '') {
      filtered = filtered.filter(product => {
        const price = product.price;
        const minPrice = priceRange.min === '' ? 0 : parseFloat(priceRange.min);
        const maxPrice = priceRange.max === '' ? Infinity : parseFloat(priceRange.max);
        return price >= minPrice && price <= maxPrice;
      });
    }

    // GST percentage filter
    if (selectedGstRates.size > 0) {
      filtered = filtered.filter(product =>
        selectedGstRates.has(product.gstPercentage)
      );
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.addedDate) - new Date(a.addedDate));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.addedDate) - new Date(b.addedDate));
        break;
      case 'name_asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price_low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'stock_high':
        filtered.sort((a, b) => b.stockCount - a.stockCount);
        break;
      case 'stock_low':
        filtered.sort((a, b) => a.stockCount - b.stockCount);
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
  };

  // Advanced filter handlers
  const handleCategoryFilter = (categoryName) => {
    const newCategories = new Set(selectedCategories);
    if (newCategories.has(categoryName)) {
      newCategories.delete(categoryName);
    } else {
      newCategories.add(categoryName);
    }
    setSelectedCategories(newCategories);
  };

  const handleStockStatusFilter = (status) => {
    const newStatus = new Set(selectedStockStatus);
    if (newStatus.has(status)) {
      newStatus.delete(status);
    } else {
      newStatus.add(status);
    }
    setSelectedStockStatus(newStatus);
  };

  const handlePriceRangeChange = (type, value) => {
    setPriceRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleGstRateFilter = (rate) => {
    const newRates = new Set(selectedGstRates);
    if (newRates.has(rate)) {
      newRates.delete(rate);
    } else {
      newRates.add(rate);
    }
    setSelectedGstRates(newRates);
  };

  const clearAllAdvancedFilters = () => {
    setSelectedCategories(new Set());
    setSelectedStockStatus(new Set());
    setPriceRange({ min: '', max: '' });
    setSelectedGstRates(new Set());
  };

  // Helper functions for counts
  const getCategoryCount = (categoryName) => {
    return products.filter(product => product.category === categoryName).length;
  };

  const getStockStatusCount = (status) => {
    return products.filter(product => product.stockStatus === status).length;
  };

  const getGstRateCount = (rate) => {
    return products.filter(product => product.gstPercentage === rate).length;
  };

  const getUniqueGstRates = () => {
    const rates = [...new Set(products.map(product => product.gstPercentage))];
    return rates.sort((a, b) => a - b);
  };

  const handleFilterToggle = (filter) => {
    const newFilters = new Set(activeFilters);

    if (filter === 'all') {
      setActiveFilters(new Set(['all']));
      return;
    }

    newFilters.delete('all');

    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }

    if (newFilters.size === 0) {
      newFilters.add('all');
    }

    setActiveFilters(newFilters);
  };

  const getStockStatusDisplay = (status) => {
    switch (status) {
      case 'in_stock':
        return { text: 'IN STOCK', class: 'stock-in' };
      case 'low_stock':
        return { text: 'LOW STOCK', class: 'stock-low' };
      case 'out_of_stock':
        return { text: 'OUT OF STOCK', class: 'stock-out' };
      default:
        return { text: 'UNKNOWN', class: 'stock-out' };
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hr${hours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  // Dynamic quick filters based on categories and common filters
  const getQuickFilters = () => {
    const baseFilters = [
      { id: 'all', label: 'All', icon: null },
      { id: 'in_stock', label: 'In Stock', icon: null },
      { id: 'low_stock', label: 'Low Stock', icon: null },
      { id: 'out_of_stock', label: 'Out of Stock', icon: null }
    ];

    // Add category filters (limit to first 4 categories for space)
    const categoryFilters = categories.slice(0, 4).map(cat => ({
      id: cat.name.toLowerCase(),
      label: cat.name.length > 10 ? cat.name.substring(0, 10) + '...' : cat.name,
      icon: null
    }));

    return [...baseFilters, ...categoryFilters];
  };

  const quickFilters = getQuickFilters();

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="product-catalog">
      <style jsx>{`
        :root {
          --in-stock-bg: #E6F0EB;
          --in-stock-text: #0A5F38;
          --in-stock-border: #0A5F38;
          
          --low-stock-bg: #F9F5F0;
          --low-stock-text: #92400e;
          --low-stock-border: #C5A57D;
          
          --out-stock-bg: #fef2f2;
          --out-stock-text: #991b1b;
          --out-stock-border: #ef4444;
          
          --text-primary: #1f2937;
          --text-secondary: #6b7280;
          --text-tertiary: #9ca3af;
        }

        .product-catalog {
          min-height: 100vh;
          background-color: #F6F8F5;
          padding: 20px 0;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 30px;
        }

        /* Header Section */
        .catalog-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 60px;
          padding: 20px 0;
          margin-bottom: 20px;
        }

        .catalog-title {
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .sort-dropdown {
          position: relative;
          width: 160px;
        }

        .sort-select {
          width: 100%;
          height: 40px;
          padding: 0 40px 0 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: white;
          font-size: 14px;
          color: var(--text-primary);
          cursor: pointer;
          appearance: none;
        }

        .sort-dropdown::after {
          content: '';
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid #6b7280;
          pointer-events: none;
        }

        /* Filter Bar */
        .filter-bar {
          background: white;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
          box-shadow: 0 10px 15px -3px rgba(26, 82, 53, 0.05);
          border: 1px solid #F1F5F9;
          min-height: 140px;
        }

        .filter-row {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }

        .search-container {
          position: relative;
          width: 300px;
        }

        .search-input {
          width: 100%;
          height: 40px;
          padding: 0 40px 0 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          color: var(--text-primary);
        }

        .search-input:focus {
          outline: none;
          border-color: #C5A57D;
          box-shadow: 0 0 0 3px rgba(197, 165, 125, 0.1);
        }

        .search-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary);
        }

        .action-buttons {
          display: flex;
          gap: 12px;
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 40px;
          padding: 0 16px;
          background: #0A5F38;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          width: 140px;
          justify-content: center;
        }

        .action-button:hover {
          background: #084c2e;
        }

        .action-button.secondary {
          background: #f3f4f6;
          color: var(--text-primary);
          border: 1px solid #d1d5db;
        }

        .action-button.secondary:hover {
          background: #e5e7eb;
        }

        .quick-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .quick-filter {
          height: 35px;
          padding: 0 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: var(--text-secondary);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          width: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quick-filter.active {
          background: #0A5F38;
          color: white;
          border-color: #0A5F38;
        }

        .quick-filter:hover {
          border-color: #0A5F38;
          color: #0A5F38;
        }

        .quick-filter.active:hover {
          color: white;
        }

        .advanced-filters-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary);
          font-size: 14px;
          cursor: pointer;
          padding: 8px 0;
          border: none;
          background: none;
        }

        /* Advanced Filters Styles */
        .advanced-filters-section {
          margin-top: 16px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .advanced-filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .advanced-filters-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .clear-filters-btn {
          background: none;
          border: 1px solid #d1d5db;
          color: var(--text-secondary);
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .clear-filters-btn:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .advanced-filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 24px;
        }

        .filter-group {
          background: white;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .filter-group h5 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .filter-options {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 13px;
          color: var(--text-secondary);
          padding: 4px 0;
        }

        .filter-checkbox input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .filter-checkbox:hover {
          color: var(--text-primary);
        }

        .price-range-inputs {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .price-input-group {
          display: flex;
          align-items: center;
          gap: 4px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          padding: 8px;
          background: white;
        }

        .price-input-group span {
          color: var(--text-secondary);
          font-size: 14px;
        }

        .price-input {
          border: none;
          outline: none;
          width: 60px;
          font-size: 14px;
          color: var(--text-primary);
        }

        .price-separator {
          font-size: 13px;
          color: var(--text-secondary);
        }

        /* Pagination Styles */
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 24px;
          padding: 20px 0;
        }

        .pagination-info {
          font-size: 14px;
          color: var(--text-secondary);
          margin-right: 16px;
        }

        .pagination-button {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 36px;
          height: 36px;
          padding: 0 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .pagination-button:hover:not(:disabled) {
          border-color: #0A5F38;
          color: #0A5F38;
          background: #E6F0EB;
        }

        .pagination-button.active {
          background: #0A5F38;
          color: white;
          border-color: #0A5F38;
        }

        .pagination-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-ellipsis {
          padding: 0 8px;
          color: var(--text-secondary);
        }

        /* Product List */
        .product-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .product-card {
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          padding: 0 24px;
          height: 100px;
          box-shadow: 0 10px 15px -3px rgba(26, 82, 53, 0.05);
          border: 1px solid #F1F5F9;
          transition: all 0.2s;
          cursor: pointer;
        }

        .product-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }

        .product-left {
          display: flex;
          align-items: center;
          width: 70%;
          gap: 16px;
        }

        .product-checkbox {
          width: 20px;
          height: 20px;
          cursor: pointer;
        }

        .product-image {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-tertiary);
        }

        .product-info {
          flex: 1;
        }

        .product-name {
          font-size: clamp(14px, 1.6vw, 16px);
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 4px 0;
        }

        .product-meta {
          font-size: clamp(12px, 1.4vw, 14px);
          color: var(--text-secondary);
          margin: 0 0 4px 0;
        }

        .product-codes {
          font-size: clamp(11px, 1.2vw, 13px);
          color: var(--text-tertiary);
          margin: 0;
        }

        .product-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          width: 30%;
          gap: 24px;
        }

        .stock-info {
          text-align: center;
        }

        .stock-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          width: 100px;
          text-align: center;
          margin-bottom: 4px;
        }

        .stock-in {
          background: var(--in-stock-bg);
          color: var(--in-stock-text);
          border: 1px solid var(--in-stock-border);
        }

        .stock-low {
          background: var(--low-stock-bg);
          color: var(--low-stock-text);
          border: 1px solid var(--low-stock-border);
        }

        .stock-out {
          background: var(--out-stock-bg);
          color: var(--out-stock-text);
          border: 1px solid var(--out-stock-border);
        }

        .stock-count {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .product-price {
          font-size: clamp(16px, 1.8vw, 18px);
          font-weight: 700;
          color: var(--text-primary);
        }

        .loading-state {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          color: var(--text-secondary);
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: var(--text-secondary);
        }

        /* Tablet Responsive */
        @media (max-width: 1199px) {
          .container {
            padding: 0 20px;
            max-width: 100%;
          }
          
          .filter-bar {
            min-height: 160px;
          }
          
          .search-container {
            width: 250px;
          }
          
          .action-button {
            width: 120px;
          }
          
          .quick-filter {
            width: 110px;
            margin: 4px;
          }
          
          .product-card {
            height: 120px;
            padding: 0 16px;
          }
          
          .product-left {
            width: 65%;
          }
          
          .product-right {
            width: 35%;
            padding-right: 16px;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 767px) {
          .container {
            padding: 0 16px;
          }
          
          .catalog-header {
            flex-direction: column;
            height: auto;
            gap: 12px;
            padding: 16px 0;
            align-items: stretch;
          }

          .sort-dropdown {
            width: 100%;
          }
          
          .filter-bar {
            height: auto;
            padding: 12px;
          }
          
          .filter-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          
          .search-container {
            width: 100%;
          }
          
          .action-buttons {
            display: flex;
            gap: 8px;
          }
          
          .action-button {
            flex: 1;
            min-width: 0;
            width: auto;
          }
          
          .quick-filters {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 6px;
          }
          
          .quick-filter {
            width: 100%;
            margin: 0;
          }
          
          .product-card {
            height: auto;
            flex-direction: column;
            padding: 16px;
            margin-bottom: 12px;
            align-items: stretch;
          }
          
          .product-left, .product-right {
            width: 100%;
          }
          
          .product-left {
            margin-bottom: 12px;
          }
          
          .product-right {
            padding-right: 0;
            justify-content: space-between;
            align-items: center;
          }
          
          .product-image {
            width: 50px;
            height: 50px;
          }

          .stock-info {
            text-align: right;
          }
        }

        /* ============================================
           RESPONSIVE DESIGN - MOBILE FIRST
           ============================================ */
        
        @media (max-width: 768px) {
          .product-catalog {
            padding: 10px 0;
          }

          .container {
            padding: 0 15px;
          }

          .catalog-header {
            flex-direction: column;
            height: auto;
            gap: 15px;
            align-items: flex-start;
          }

          .catalog-title {
            font-size: 20px;
          }

          .sort-dropdown {
            width: 100%;
          }

          /* Search and Filter Section */
          .search-filter-section {
            flex-direction: column;
            gap: 12px;
          }

          .search-container {
            width: 100% !important;
          }

          .filter-buttons {
            flex-direction: column;
            width: 100%;
          }

          .filter-buttons button {
            width: 100%;
            justify-content: center;
          }

          /* Quick Filters */
          .quick-filters {
            flex-wrap: wrap;
            gap: 8px;
          }

          .quick-filter {
            font-size: 12px;
            padding: 6px 12px;
          }

          /* Advanced Filters */
          .advanced-filters-panel {
            padding: 15px;
          }

          .filter-group {
            margin-bottom: 15px;
          }

          .filter-group-title {
            font-size: 13px;
          }

          /* Product Cards */
          .product-card {
            flex-direction: column;
            padding: 15px;
          }

          .product-left {
            width: 100%;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .product-image {
            width: 100%;
            height: auto;
            aspect-ratio: 16/9;
            object-fit: cover;
          }

          .product-info {
            width: 100%;
          }

          .product-name {
            font-size: 16px;
          }

          .product-meta {
            flex-wrap: wrap;
            gap: 8px;
          }

          .meta-item {
            font-size: 11px;
          }

          .product-right {
            width: 100%;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid #e5e7eb;
          }

          .stock-info {
            flex-direction: row;
            align-items: center;
            gap: 10px;
          }

          .stock-badge {
            font-size: 10px;
            padding: 4px 8px;
          }

          .stock-count {
            font-size: 12px;
          }

          .price-section {
            text-align: right;
          }

          .product-price {
            font-size: 18px;
          }

          .price-label {
            font-size: 10px;
          }

          /* Pagination */
          .pagination {
            flex-wrap: wrap;
            gap: 5px;
          }

          .pagination-button {
            min-width: 35px;
            height: 35px;
            font-size: 13px;
          }

          .pagination-info {
            font-size: 12px;
            width: 100%;
            text-align: center;
            order: -1;
            margin-bottom: 10px;
          }
        }

        @media (max-width: 480px) {
          .catalog-title {
            font-size: 18px;
          }

          .product-name {
            font-size: 14px;
          }

          .product-price {
            font-size: 16px;
          }

          .meta-item {
            font-size: 10px;
          }
        }
      `}</style>

      <div className="container">
        {/* Header */}
        <div className="catalog-header">
          <h1 className="catalog-title">Product Catalog</h1>
          <div className="sort-dropdown">
            <select
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
              <option value="price_low">Price Low-High</option>
              <option value="price_high">Price High-Low</option>
              <option value="stock_high">Stock High-Low</option>
              <option value="stock_low">Stock Low-High</option>
            </select>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="filter-bar">
          <div className="filter-row">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search products..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="search-icon" size={16} />
            </div>

            <div className="action-buttons">
              <button className="action-button secondary">
                <Download size={16} />
                Export
              </button>
            </div>
          </div>

          <div className="filter-row">
            <div className="quick-filters">
              {quickFilters.map(filter => (
                <button
                  key={filter.id}
                  className={`quick-filter ${activeFilters.has(filter.id) ? 'active' : ''}`}
                  onClick={() => handleFilterToggle(filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <button
            className="advanced-filters-toggle"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <Filter size={14} />
            Advanced Filters
            <ChevronDown size={14} style={{
              transform: showAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} />
          </button>

          {/* Advanced Filters Section */}
          {showAdvancedFilters && (
            <div className="advanced-filters-section">
              <div className="advanced-filters-header">
                <h4>Advanced Filters</h4>
                <button className="clear-filters-btn" onClick={clearAllAdvancedFilters}>
                  Clear All
                </button>
              </div>

              <div className="advanced-filters-grid">
                {/* Category Filter */}
                <div className="filter-group">
                  <h5>Category</h5>
                  <div className="filter-options">
                    {categories.map(category => (
                      <label key={category.id} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCategories.has(category.name)}
                          onChange={() => handleCategoryFilter(category.name)}
                        />
                        <span className="checkmark"></span>
                        {category.name} ({getCategoryCount(category.name)})
                      </label>
                    ))}
                  </div>
                </div>

                {/* Stock Status Filter */}
                <div className="filter-group">
                  <h5>Stock Status</h5>
                  <div className="filter-options">
                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedStockStatus.has('in_stock')}
                        onChange={() => handleStockStatusFilter('in_stock')}
                      />
                      <span className="checkmark"></span>
                      In Stock ({getStockStatusCount('in_stock')})
                    </label>
                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedStockStatus.has('low_stock')}
                        onChange={() => handleStockStatusFilter('low_stock')}
                      />
                      <span className="checkmark"></span>
                      Low Stock ({getStockStatusCount('low_stock')})
                    </label>
                    <label className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedStockStatus.has('out_of_stock')}
                        onChange={() => handleStockStatusFilter('out_of_stock')}
                      />
                      <span className="checkmark"></span>
                      Out of Stock ({getStockStatusCount('out_of_stock')})
                    </label>
                  </div>
                </div>

                {/* Price Range Filter */}
                <div className="filter-group">
                  <h5>Price Range</h5>
                  <div className="price-range-inputs">
                    <div className="price-input-group">
                      <span>₹</span>
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min}
                        onChange={(e) => handlePriceRangeChange('min', e.target.value)}
                        className="price-input"
                      />
                    </div>
                    <span className="price-separator">to</span>
                    <div className="price-input-group">
                      <span>₹</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max}
                        onChange={(e) => handlePriceRangeChange('max', e.target.value)}
                        className="price-input"
                      />
                    </div>
                  </div>
                </div>

                {/* GST Percentage Filter */}
                <div className="filter-group">
                  <h5>GST Percentage</h5>
                  <div className="filter-options">
                    {getUniqueGstRates().map(rate => (
                      <label key={rate} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedGstRates.has(rate)}
                          onChange={() => handleGstRateFilter(rate)}
                        />
                        <span className="checkmark"></span>
                        {rate}% ({getGstRateCount(rate)})
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Product List */}
        <div className="product-list">
          {loading ? (
            <div className="loading-state">
              <div>Loading products...</div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="empty-state">
              <Package size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <h3>No products found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            currentProducts.map(product => {
              const stockStatus = getStockStatusDisplay(product.stockStatus);
              return (
                <div key={product.id} className="product-card">
                  <div className="product-left">
                    <div className="product-image">
                      <Package size={24} />
                    </div>

                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-meta">
                        {product.category} • {formatTimeAgo(product.addedDate)}
                      </p>
                      <p className="product-codes">
                        SKU: {product.sku} | HSN: {product.hsn}
                      </p>
                    </div>
                  </div>

                  <div className="product-right">
                    <div className="stock-info">
                      <div className={`stock-badge ${stockStatus.class}`}>
                        {stockStatus.text}
                      </div>
                      <div className="stock-count">
                        Stock: {product.stockCount}
                      </div>
                    </div>

                    <div className="product-price">
                      ₹{product.price.toFixed(1)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredProducts.length > 0 && (
          <div className="pagination-container">
            <span className="pagination-info">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredProducts.length)} of {filteredProducts.length}
            </span>

            <button
              className="pagination-button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;

              // Show first page, last page, current page, and pages around current
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}

            <button
              className="pagination-button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProductCatalogPage;
